import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Trash2, Send, MessageSquare } from "lucide-react";
import { API, authHeaders, formatApiErrorDetail } from "./api";

const CATEGORIES = ["General", "Call", "Meeting", "Email", "Lender", "Document", "Advice"];

const fmtDMY = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso.length === 10 ? `${iso}T00:00:00` : iso);
  if (isNaN(d)) return "—";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  return `${dd}/${mm}/${d.getFullYear()}`;
};

const initials = (email) => {
  if (!email) return "ST";
  const name = email.split("@")[0] || "";
  return (name.replace(/[^a-z]/gi, " ").trim().split(/\s+/).slice(0, 2).map(w => w[0]).join("") || "ST").toUpperCase();
};

export const CustomerNotes = ({ customerId }) => {
  const [rows, setRows] = useState([]);
  const [body, setBody] = useState("");
  const [category, setCategory] = useState("General");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/customers/${customerId}/notes`, authHeaders());
      setRows(data || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not load notes");
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [customerId]);

  const add = async () => {
    const trimmed = body.trim();
    if (!trimmed) return;
    setSaving(true);
    try {
      await axios.post(`${API}/admin/customers/${customerId}/notes`,
        { body: trimmed, category }, authHeaders());
      setBody("");
      setCategory("General");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not add note");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this note?")) return;
    try {
      await axios.delete(`${API}/admin/customers/${customerId}/notes/${id}`, authHeaders());
      setRows((r) => r.filter((x) => x.id !== id));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not delete");
    }
  };

  return (
    <div className="space-y-4" data-testid="customer-notes">
      {/* Compose */}
      <div className="bg-white border border-forest/15 rounded-sm p-4">
        <label className="block text-[10.5px] tracking-[0.18em] uppercase font-bold text-forest/60 mb-1.5">
          Add internal note
        </label>
        <textarea
          rows={3}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          maxLength={4000}
          data-testid="customer-notes-body"
          placeholder="e.g. Called client — asked for 3 months bank statements by Friday."
          className="w-full bg-cream-3 border border-forest/15 rounded-sm px-3 py-2 text-sm focus:border-gold focus:outline-none"
        />
        <div className="flex items-center justify-between gap-2 mt-2 flex-wrap">
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            data-testid="customer-notes-category"
            className="bg-white border border-forest/15 rounded-sm px-2.5 py-1.5 text-[12px] text-forest focus:border-gold focus:outline-none"
          >
            {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <button
            type="button"
            onClick={add}
            disabled={saving || !body.trim()}
            data-testid="customer-notes-save"
            className="btn-gold disabled:opacity-50 inline-flex items-center gap-1.5 !py-1.5"
          >
            <Send size={13} /> {saving ? "Saving…" : "Save note"}
          </button>
        </div>
      </div>

      {/* Timeline */}
      {rows.length === 0 ? (
        <div className="bg-white border border-forest/10 rounded-sm px-5 py-10 text-center">
          <MessageSquare size={22} className="mx-auto text-sage mb-2" />
          <p className="text-[13px] text-sage">No notes yet. Add the first one above.</p>
        </div>
      ) : (
        <ul className="space-y-2.5">
          {rows.map((n) => (
            <li
              key={n.id}
              data-testid={`customer-note-${n.id}`}
              className="bg-white border border-forest/10 rounded-sm px-4 py-3 flex gap-3"
            >
              <span className="shrink-0 w-9 h-9 rounded-full bg-forest text-cream border border-gold/30 flex items-center justify-center font-bold text-[11px] tracking-wider">
                {initials(n.author_email)}
              </span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-[13px] font-semibold text-forest">{n.author_email || "Staff"}</p>
                  <span className="text-[9.5px] tracking-[0.14em] uppercase font-bold px-1.5 py-0.5 border border-gold/40 text-gold-dark bg-cream-3 rounded-sm">
                    {n.category || "General"}
                  </span>
                  <span className="text-[11px] text-sage">· {fmtDMY(n.created_at)}</span>
                </div>
                <p className="text-[13px] text-ink mt-1 whitespace-pre-wrap break-words">{n.body}</p>
              </div>
              <button
                type="button"
                onClick={() => remove(n.id)}
                data-testid={`customer-note-delete-${n.id}`}
                className="shrink-0 p-1.5 text-forest/50 hover:text-rose-600"
                title="Delete this note"
              >
                <Trash2 size={14} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
